import React, { type ComponentPropsWithoutRef, useMemo } from 'react';
import { ModalTransition } from '@atlaskit/modal-dialog';
import type { JiraEntryPointContainer } from '@atlassian/jira-entry-point-container/src/index.tsx';
import { useEntryPointSubscription } from '@atlassian/jira-entry-point-subscription/src/index.tsx';
import type {
	AnyEntryPoint,
	RuntimePropsOfEntryPoint,
} from '@atlassian/jira-entry-point/src/common/types.tsx';
import type { EntryPointActions } from '@atlassian/jira-entry-point/src/controllers/utils/types.tsx';
import { InternalEntryPointContainer } from '@atlassian/jira-internal-entry-point-container/src/index.tsx';

interface Props<TEntryPoint extends AnyEntryPoint>
	extends Omit<
		ComponentPropsWithoutRef<typeof JiraEntryPointContainer<TEntryPoint>>,
		'fallback' | 'runtimeProps'
	> {
	entryPointActions: EntryPointActions;
	runtimeProps: Omit<RuntimePropsOfEntryPoint<TEntryPoint>, 'onClose'> & { onClose?: () => void };
}

/**
 * @deprecated Use Modal EntryPoint API instead instead. https://go/modal-entry-point-api
 * */
export const ModalEntryPointContainer = <TEntryPoint extends AnyEntryPoint>({
	entryPointActions: { unload },
	entryPointReferenceSubject,
	errorFallback,
	runtimeProps,
	...containerProps
}: Props<TEntryPoint>) => {
	const entryPointReference = useEntryPointSubscription<TEntryPoint>(entryPointReferenceSubject);
	const props = useMemo(
		() =>
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			({
				...runtimeProps,
				onClose() {
					runtimeProps.onClose?.();
					unload();
				},
			}) as RuntimePropsOfEntryPoint<TEntryPoint>,
		[runtimeProps, unload],
	);

	return (
		<ModalTransition>
			{entryPointReference && (
				<InternalEntryPointContainer
					{...containerProps}
					entryPointReference={entryPointReference}
					errorFallback={errorFallback || 'flag'}
					placeholderName="modal-entry-point-container"
					runtimeProps={props}
				/>
			)}
		</ModalTransition>
	);
};
